import { c as _c } from "react/compiler-runtime";
import { useTopBar, TOPBAR_NOTIFICATIONS, TopBarNotification } from '@/context/TopBarContext';
import { useSubscription } from '@/context/SubscriptionProvider';
import { useI18n } from '@/context/I18nProvider';
import { useInstagramConnection } from '@/hooks/instagram/useInstagramConnection';
import { ApiClient } from '@/services/api-client';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useCallback } from 'react';
const LOW_CREDIT_THRESHOLD_CENTS = 100; // $1.00
const EXPIRY_WARNING_DAYS = 7;

// ---------------------------------------------------------------------------
// Map backend FailureReason values → registry entries (actionable only)
// ---------------------------------------------------------------------------

const FAILURE_REASON_TO_NOTIFICATION = {
  no_sequence: TOPBAR_NOTIFICATIONS.leadFailedNoSequence,
  no_whatsapp_profile: TOPBAR_NOTIFICATIONS.leadFailedNoWhatsappProfile,
  no_whatsapp_phone: TOPBAR_NOTIFICATIONS.leadFailedNoWhatsappPhone,
  payment_failure: TOPBAR_NOTIFICATIONS.leadFailedPaymentFailure,
  rate_limit_exceeded: TOPBAR_NOTIFICATIONS.leadFailedRateLimit
} as const;

// ---------------------------------------------------------------------------
// Helper — build a TopBarNotification from a registry entry
// ---------------------------------------------------------------------------

type RegistryEntry = (typeof TOPBAR_NOTIFICATIONS)[keyof typeof TOPBAR_NOTIFICATIONS];

/** Loose translator type — accepts any string key (registry keys are dynamic). */
type TranslateFn = (key: string, vars?: Record<string, string>) => string;

/**
 * Build a TopBarNotification from a registry config.
 * When `entityId` is provided the notification id becomes `{cfg.id}:{entityId}`
 * so that each account / profile gets its own dismiss state.
 * `linkHrefOverride` lets per-account notifications point to a profile-specific page.
 */
function buildNotification(cfg: RegistryEntry, t: TranslateFn, messageVars?: Record<string, string>, messageSuffix?: string, entityId?: string, linkHrefOverride?: string): TopBarNotification {
  return {
    id: entityId ? `${cfg.id}:${entityId}` : cfg.id,
    type: cfg.type,
    priority: cfg.priority,
    message: t(cfg.messageKey, messageVars) + (messageSuffix ?? ''),
    link: cfg.linkLabelKey && (linkHrefOverride || cfg.linkHref) ? {
      label: t(cfg.linkLabelKey),
      href: linkHrefOverride ?? cfg.linkHref!
    } : undefined
  };
}

// ---------------------------------------------------------------------------
// Headless component — watches all data sources, pushes / removes alerts
// ---------------------------------------------------------------------------

const TopBarNotificationTriggers = () => {
  const $ = _c(59);
  const {
    subscription
  } = useSubscription();
  const {
    pushNotification,
    removeNotification,
    removeNotificationsByPrefix
  } = useTopBar();
  const {
    t
  } = useI18n();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: ["billing.getAllWallets"],
      queryFn: _temp,
      staleTime: Infinity,
      refetchOnWindowFocus: false
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data: walletRes
  } = useQuery(t0);
  const wallet = walletRes?.data?.Wallets?.[0] ?? null;
  const {
    connectedAccounts: igAccounts
  } = useInstagramConnection();
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      queryKey: ["integration.getWhatsappProfiles"],
      queryFn: _temp2,
      staleTime: Infinity,
      refetchOnWindowFocus: false
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: waProfilesRes
  } = useQuery(t1);
  const waProfiles = waProfilesRes?.data;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      queryKey: ["sequences.getSequences"],
      queryFn: _temp3,
      staleTime: Infinity,
      refetchOnWindowFocus: false
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    data: sequencesRes
  } = useQuery(t2);
  const sequences = sequencesRes?.data;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      queryKey: ["organization.failedLeadJobsSummary"],
      queryFn: _temp4,
      staleTime: Infinity,
      refetchOnWindowFocus: false
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    data: failedJobsRes
  } = useQuery(t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      queryKey: ["organization.ignoredInboundEventsSummary"],
      queryFn: _temp5,
      staleTime: Infinity,
      refetchOnWindowFocus: false
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const {
    data: ignoredEventsRes
  } = useQuery(t4);
  const translate = t as TranslateFn;
  let t5;
  if ($[5] !== pushNotification || $[6] !== removeNotification || $[7] !== translate) {
    t5 = (condition, cfg, vars, suffix) => {
      if (condition) {
        pushNotification(buildNotification(cfg, translate, vars, suffix));
      } else {
        removeNotification(cfg.id);
      }
    };
    $[5] = pushNotification;
    $[6] = removeNotification;
    $[7] = translate;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const toggle = t5;
  let t6;
  if ($[9] !== pushNotification || $[10] !== removeNotification || $[11] !== translate) {
    t6 = (condition_0, cfg_0, entityId, vars_0, linkHrefOverride) => {
      const fullId = `${cfg_0.id}:${entityId}`;
      if (condition_0) {
        pushNotification(buildNotification(cfg_0, translate, vars_0, undefined, entityId, linkHrefOverride));
      } else {
        removeNotification(fullId);
      }
    };
    $[9] = pushNotification;
    $[10] = removeNotification;
    $[11] = translate;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const togglePerAccount = t6;
  let t7;
  let t8;
  if ($[13] !== removeNotification || $[14] !== subscription || $[15] !== toggle) {
    t7 = () => {
      if (!subscription) {
        return;
      }
      const isExpiring = subscription.CancelAtPeriodEnd || subscription.Status === "CANCELED" || subscription.Status === "EXPIRED" || subscription.Status === "PAYMENT_DUE" || subscription.Status === "UNPAID";
      if (!isExpiring) {
        removeNotification(TOPBAR_NOTIFICATIONS.subscriptionExpiring.id);
        return;
      }
      const periodEnd = new Date(subscription.CurrentPeriodEnd);
      const daysLeft = Math.ceil((periodEnd.getTime() - Date.now()) / 86400000);
      const isCanceled = subscription.Status === "CANCELED";
      const messageKey = daysLeft < 0 ? isCanceled ? "topbar.subscriptionCanceled" : "topbar.subscriptionExpired" : daysLeft === 0 ? "topbar.subscriptionExpiresToday" : daysLeft === 1 ? "topbar.subscriptionExpiresTomorrow" : "topbar.subscriptionExpiring";
      toggle(daysLeft <= EXPIRY_WARNING_DAYS, {
        ...TOPBAR_NOTIFICATIONS.subscriptionExpiring,
        messageKey
      } as RegistryEntry, {
        days: String(daysLeft)
      });
    };
    t8 = [subscription, toggle, removeNotification];
    $[13] = removeNotification;
    $[14] = subscription;
    $[15] = toggle;
    $[16] = t7;
    $[17] = t8;
  } else {
    t7 = $[16];
    t8 = $[17];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[18] !== removeNotification || $[19] !== subscription || $[20] !== toggle || $[21] !== translate || $[22] !== wallet) {
    t9 = () => {
      if (!wallet || !subscription) {
        removeNotification(TOPBAR_NOTIFICATIONS.lowCredits.id);
        return;
      }
      const isLow = wallet.TokensRemaining < LOW_CREDIT_THRESHOLD_CENTS;
      const suffix_0 = isLow && !wallet.AutoRechargeEnabled ? ` ${translate("topbar.autoTopUpHint")}` : "";
      toggle(isLow, TOPBAR_NOTIFICATIONS.lowCredits, undefined, suffix_0);
    };
    t10 = [wallet, subscription, toggle, removeNotification, translate];
    $[18] = removeNotification;
    $[19] = subscription;
    $[20] = toggle;
    $[21] = translate;
    $[22] = wallet;
    $[23] = t10;
    $[24] = t9;
  } else {
    t10 = $[23];
    t9 = $[24];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[25] !== removeNotificationsByPrefix || $[26] !== togglePerAccount || $[27] !== waProfiles) {
    t11 = () => {
      if (!waProfiles) {
        return;
      }
      removeNotificationsByPrefix(TOPBAR_NOTIFICATIONS.whatsappBanned.id);
      removeNotificationsByPrefix(TOPBAR_NOTIFICATIONS.whatsappPaymentFailure.id);
      removeNotificationsByPrefix(TOPBAR_NOTIFICATIONS.whatsappInactive.id);
      removeNotificationsByPrefix(TOPBAR_NOTIFICATIONS.personalWhatsappDisconnected.id);
      for (const p of waProfiles) {
        const name = p.ProfileName || p.ProfileID;
        const isPersonal = p.IntegrationMethod === "PERSONAL_QR";
        const profileLink = isPersonal ? `/integrations/personal-whatsapp/${p.ProfileID}` : `/integrations/whatsapp/profile/${p.ProfileID}`;
        togglePerAccount(p.IsBanned, TOPBAR_NOTIFICATIONS.whatsappBanned, p.ProfileID, {
          name
        }, profileLink);
        togglePerAccount(p.HasPaymentFailure && !p.IsBanned, TOPBAR_NOTIFICATIONS.whatsappPaymentFailure, p.ProfileID, {
          name
        }, profileLink);
        togglePerAccount(isPersonal && !!p.IsDisconnected && !p.IsBanned, TOPBAR_NOTIFICATIONS.personalWhatsappDisconnected, p.ProfileID, {
          name
        }, profileLink);
        togglePerAccount(!isPersonal && p.ProfileStatus === "INACTIVE" && !p.IsBanned && !p.HasPaymentFailure, TOPBAR_NOTIFICATIONS.whatsappInactive, p.ProfileID, {
          name
        }, profileLink);
      }
    };
    t12 = [waProfiles, togglePerAccount, removeNotificationsByPrefix];
    $[25] = removeNotificationsByPrefix;
    $[26] = togglePerAccount;
    $[27] = waProfiles;
    $[28] = t11;
    $[29] = t12;
  } else {
    t11 = $[28];
    t12 = $[29];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[30] !== igAccounts || $[31] !== removeNotificationsByPrefix || $[32] !== togglePerAccount) {
    t13 = () => {
      if (!igAccounts) {
        return;
      }
      removeNotificationsByPrefix(TOPBAR_NOTIFICATIONS.instagramDisconnected.id);
      removeNotificationsByPrefix(TOPBAR_NOTIFICATIONS.instagramPaused.id);
      for (const a of igAccounts) {
        const entityId_0 = a.InstagramUserId;
        const name_0 = a.Username || a.ProfileName || entityId_0;
        togglePerAccount(a.Status === "DISCONNECTED", TOPBAR_NOTIFICATIONS.instagramDisconnected, entityId_0, {
          name: name_0
        });
        togglePerAccount(a.Status === "PAUSED", TOPBAR_NOTIFICATIONS.instagramPaused, entityId_0, {
          name: name_0
        });
      }
    };
    t14 = [igAccounts, togglePerAccount, removeNotificationsByPrefix];
    $[30] = igAccounts;
    $[31] = removeNotificationsByPrefix;
    $[32] = togglePerAccount;
    $[33] = t13;
    $[34] = t14;
  } else {
    t13 = $[33];
    t14 = $[34];
  }
  useEffect(t13, t14);
  let t15;
  let t16;
  if ($[35] !== igAccounts || $[36] !== removeNotificationsByPrefix || $[37] !== sequences || $[38] !== togglePerAccount) {
    t15 = () => {
      if (!sequences || !igAccounts) {
        removeNotificationsByPrefix(TOPBAR_NOTIFICATIONS.noActiveSequences.id);
        return;
      }
      const officialTriggers = ["INSTAGRAM_COMMENT", "INSTAGRAM_MESSAGE", "INSTAGRAM_STORY_REPLY"];
      removeNotificationsByPrefix(TOPBAR_NOTIFICATIONS.noActiveSequences.id);
      for (const a_0 of igAccounts) {
        if (a_0.Status !== "ACTIVE") {
          continue;
        }
        const entityId_1 = a_0.InstagramUserId;
        const name_1 = a_0.Username || a_0.ProfileName || entityId_1;
        const hasOfficialSequence = sequences.some(s => s.Active && s.ProfileID === entityId_1 && officialTriggers.includes(s.Trigger));
        togglePerAccount(!hasOfficialSequence, TOPBAR_NOTIFICATIONS.noActiveSequences, entityId_1, {
          name: name_1
        });
      }
    };
    t16 = [sequences, igAccounts, togglePerAccount, removeNotificationsByPrefix];
    $[35] = igAccounts;
    $[36] = removeNotificationsByPrefix;
    $[37] = sequences;
    $[38] = togglePerAccount;
    $[39] = t15;
    $[40] = t16;
  } else {
    t15 = $[39];
    t16 = $[40];
  }
  useEffect(t15, t16);
  let t17;
  if ($[41] !== igAccounts || $[42] !== ignoredEventsRes?.data?.Items || $[43] !== removeNotificationsByPrefix || $[44] !== togglePerAccount) {
    t17 = () => {
      const items = ignoredEventsRes?.data?.Items;
      if (!items) {
        return;
      }
      removeNotificationsByPrefix(TOPBAR_NOTIFICATIONS.storyRepliesIgnored.id);
      removeNotificationsByPrefix(TOPBAR_NOTIFICATIONS.commentsIgnored.id);
      const nameFor = profileId => {
        const account = igAccounts?.find(a_1 => a_1.InstagramUserId === profileId);
        return account?.Username || account?.ProfileName || profileId;
      };
      const TRIGGER_TO_NOTIFICATION = {
        INSTAGRAM_STORY_REPLY: TOPBAR_NOTIFICATIONS.storyRepliesIgnored,
        INSTAGRAM_COMMENT: TOPBAR_NOTIFICATIONS.commentsIgnored
      } as const;
      for (const item of items) {
        const cfg_1 = TRIGGER_TO_NOTIFICATION[item.Trigger as keyof typeof TRIGGER_TO_NOTIFICATION];
        if (!cfg_1) {
          continue;
        }
        togglePerAccount(item.Count > 0, cfg_1, item.ProfileID, {
          name: nameFor(item.ProfileID),
          count: String(item.Count)
        });
      }
    };
    $[41] = igAccounts;
    $[42] = ignoredEventsRes?.data?.Items;
    $[43] = removeNotificationsByPrefix;
    $[44] = togglePerAccount;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== igAccounts || $[47] !== ignoredEventsRes || $[48] !== removeNotificationsByPrefix || $[49] !== togglePerAccount) {
    t18 = [ignoredEventsRes, igAccounts, togglePerAccount, removeNotificationsByPrefix];
    $[46] = igAccounts;
    $[47] = ignoredEventsRes;
    $[48] = removeNotificationsByPrefix;
    $[49] = togglePerAccount;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  useEffect(t17, t18);
  let t19;
  if ($[51] !== failedJobsRes?.data?.Items || $[52] !== sequences || $[53] !== toggle) {
    t19 = () => {
      const items_0 = failedJobsRes?.data?.Items;
      if (!items_0) {
        return;
      }
      const activeReasons = new Set(items_0.map(_temp6));
      const hasActiveWhatsappSequence = sequences?.some(_temp7);
      for (const [reason, cfg_2] of Object.entries(FAILURE_REASON_TO_NOTIFICATION)) {
        const shouldShow = reason === "rate_limit_exceeded" ? activeReasons.has(reason) && !!hasActiveWhatsappSequence : activeReasons.has(reason);
        toggle(shouldShow, cfg_2);
      }
    };
    $[51] = failedJobsRes?.data?.Items;
    $[52] = sequences;
    $[53] = toggle;
    $[54] = t19;
  } else {
    t19 = $[54];
  }
  let t20;
  if ($[55] !== failedJobsRes || $[56] !== sequences || $[57] !== toggle) {
    t20 = [failedJobsRes, sequences, toggle];
    $[55] = failedJobsRes;
    $[56] = sequences;
    $[57] = toggle;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  useEffect(t19, t20);
  return null;
};
export { TopBarNotificationTriggers };
function _temp() {
  return ApiClient.billing.getAllWallets();
}
function _temp2() {
  return ApiClient.integration.getWhatsappProfiles();
}
function _temp3() {
  return ApiClient.sequences.getSequences();
}
function _temp4() {
  return ApiClient.organization.getFailedLeadJobsSummary();
}
function _temp5() {
  return ApiClient.organization.getIgnoredInboundEventsSummary();
}
function _temp6(i) {
  return i.FailureReason;
}
function _temp7(s_0) {
  return s_0.Active && s_0.Trigger === "WHATSAPP_MESSAGE";
}